/**
 * Every user-facing string this package renders, and its English default.
 *
 * This map is the key inventory: components ask for a key, and a host that
 * supplies a translator resolves it. Anything a host does not (yet) translate
 * falls back to the English value here, so adding a key is never a breaking
 * change and the standalone dev app needs no host at all.
 *
 * Placeholders use `{name}` to match `@susu/i18n`'s `interpolate`.
 */
export const DEFAULT_LABELS = {
  // Price breakdown
  'orderList.summary.subtotal': 'Subtotal',
  'orderList.summary.salesTax': 'Sales tax',
  'orderList.summary.shipping': 'Shipping',
  'orderList.summary.free': 'Free',
  'orderList.summary.total': 'Total',
  'orderList.summary.inclVat': 'incl. vat',

  // Addresses / dates card
  'orderList.purchaseDetails.title': 'Purchase details',
  'orderList.purchaseDetails.returnTitle': 'Return details',
  'orderList.purchaseDetails.shippingAddress': 'Shipping address',
  'orderList.purchaseDetails.billingAddress': 'Billing address',
  'orderList.purchaseDetails.orderDate': 'Order date',
  'orderList.purchaseDetails.orderedAt': 'Ordered at',
  'orderList.purchaseDetails.orderNumber': 'Order number',
  'orderList.purchaseDetails.online': 'Online',

  // Item list and cards
  'orderList.items.title': 'Order items',
  'orderList.item.returned': 'Returned',
  'orderList.item.cancelled': 'Canceled',
  'orderList.item.inProgress': 'In progress',
  'orderList.item.details': 'Details',
  'orderList.item.returnDetails': 'Return details',
  'orderList.item.size': 'Size {size}',
  'orderList.item.quantity': 'Qty: {quantity}',
  'orderList.item.customizations': 'Customizations',
  'orderList.item.noImage': 'No image',
  'orderList.item.copyItemCode': 'Copy item code {code}',
  'orderList.item.copyProductCode': 'Copy product code {code}',

  // Order header subtitle
  'orderList.subtitle.orderedOn': 'Ordered on {date}',
  'orderList.subtitle.deliveredOn': 'Delivered on {date}',
  'orderList.subtitle.deliveryBy': 'Delivery by {date}',

  // Timeline steps
  'orderList.timeline.ordered': 'Ordered',
  'orderList.timeline.received': 'Received',
  'orderList.timeline.inProgress': 'In progress',
  'orderList.timeline.beingAltered': 'Being altered',
  'orderList.timeline.ready': 'Ready',
  'orderList.timeline.shipped': 'Shipped',
  'orderList.timeline.delivered': 'Delivered',
  'orderList.timeline.exchanged': 'Exchanged',
  'orderList.timeline.registered': 'Registered',

  // Display statuses derived from the fulfillment status id
  'orderList.status.orderConfirmed': 'Order Confirmed',
  'orderList.status.inProgress': 'In Progress',
  'orderList.status.onItsWay': 'On its way',
  'orderList.status.readyForPickup': 'Ready for pickup',
  'orderList.status.delivered': 'Delivered',
  'orderList.status.purchased': 'Purchased',

  // Manage order actions
  'orderList.manageOrder.title': 'Manage order',
  'orderList.manageOrder.return': 'Return',
  'orderList.manageOrder.cancelOrder': 'Cancel order',
  'orderList.manageOrder.selectItems': 'Select items',
  'orderList.manageOrder.continue': 'Continue',
  'orderList.manageOrder.keepItems': 'Keep my items',
  'orderList.manageOrder.confirmCancellation': 'Confirm cancellation',
  'orderList.manageOrder.cancellationWarning':
    "This action can't be undone. Your refund will be processed and confirmed by email.",
  'orderList.manageOrder.cancelFailed': 'Failed to cancel the order',

  // Order header, list card and cancellation labels
  'orderList.header.orderNumber': 'Order no. {orderNumber}',
  'orderList.list.itemCount.one': '{count} item',
  'orderList.list.itemCount.other': '{count} items',
  'orderList.manageOrder.selectItemForCancellation':
    'Select {itemName} for cancellation',
  'orderList.manageOrder.unnamedItem': 'item',
  // FAQ and support
  'orderList.faq.title': 'FAQ & Support',
  'orderList.faq.link.faqPage': 'FAQ page',
  'orderList.faq.link.customerService': 'Customer Service',
  'orderList.faq.delivery.title': 'Delivery',
  'orderList.faq.delivery.body':
    "Suitsupply's estimated delivery time and shipping fees vary by country. We aim to deliver all orders within 3 to 4 business days worldwide, but many countries are eligible for next-day and two-day delivery. For more information on shipping & delivery, visit our {faqPage}.",
  'orderList.faq.returns.title': 'Returns and refunds',
  'orderList.faq.returns.intro':
    'You can return your items for free within 30 days from delivery or store purchase date.',
  'orderList.faq.returns.conditionsTitle': 'Items must:',
  'orderList.faq.returns.conditionUnworn':
    'Be unworn, unaltered, free from any signs of wear',
  'orderList.faq.returns.conditionTags': 'Have the original tags attached',
  'orderList.faq.returns.conditionWindow':
    'Have been delivered to you or ready for pick up at a store less than 30 days ago',
  'orderList.faq.returns.conditionCountry':
    'Be returned in the same country of purchase (cross country returns are not permitted)',
  'orderList.faq.returns.help':
    'Need help? Contact {customerService} or visit our {faqPage}.',
  'orderList.faq.needHelp.title': 'Need help?',
  'orderList.faq.needHelp.body':
    'Visit our {faqPage} or contact our customer service team for any questions about your order.',

  // Navigation
  'orderList.navigation.breadcrumb': 'Breadcrumb',
  'orderList.navigation.orders': 'Orders',
  'orderList.navigation.orderDetails': 'Order Details',
  'orderList.navigation.goBack': 'Go back',
  'orderList.navigation.back': 'Back',
  'orderList.navigation.goBackFromOrderDetails': 'Go back from order details',

  // Custom Made modal
  'orderList.customMade.fabric': 'Fabric',
  'orderList.customMade.reorder': 'Reorder',

  // List, loading and error states
  'orderList.list.loading': 'Loading orders',
  'orderList.list.empty': 'No orders found',
  'orderList.list.product': 'Product',
  'orderList.list.orderedTitle': 'Ordered {date}',
  'orderList.details.loading': 'Loading order details',
  'orderList.error.unavailable': 'Order details unavailable.',
  'orderList.error.tryAgain': 'Please try again or contact Customer Service.',
  'orderList.error.missingOrderId': 'Missing order ID or account number',
} as const

export type LabelKey = keyof typeof DEFAULT_LABELS
